import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ProspectsIcon } from '../assets';
import { Tab } from '../components/campaigns/SingleCampaignTabs';
import { SubrowTable } from '../components/campaigns/SubrowsTable';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { LateralProfileProspectMenu } from '../components/common/LateralProfileProspectMenu';
import DataTable from '../components/tables/DataTable';
import type { ExtraInfoCampaignHeader } from '../components/tables/headers/campaigns';
import { getCampaignsTableHeaders } from '../components/tables/headers/campaigns';
import { replaceIds, ROUTES } from '../config/routes/config';
import { GET_CAMPAIGNS_LIST } from '../graphql/campaign/query';
import { GET_PROSPECT } from '../graphql/prospects/query';
import { useNotification } from '../hooks/useNotification';
import { usePermissions } from '../hooks/usePermissions';
import { useTableDataAndFilters } from '../hooks/useTableDataAndFilters';
import { campaignFilterBrandAtom } from '../states/atoms';
import type {
  CampaignFilters,
  CampaignTable,
  ResCampaignsList,
} from '../types';

export const SingleProspectCampaigns = () => {
  const { id } = useParams();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { notifyWarning } = useNotification();
  const { isInfluencer } = usePermissions();
  const [brand, setBrand] = useState<any>({});

  const { data } = useQuery(GET_PROSPECT, {
    variables: { brandId: id },
    onError: () => {
      notifyWarning(t('global.errorSingleBrand'));
      navigate(ROUTES.PROSPECTS);
    },
    onCompleted: (info: any) => {
      setBrand(info.brand.data);
    },
    skip: !id,
  });

  const {
    data: campaigns,
    resetPagination,
    total,
    error,
    loading,
    filters,
    setFilters,
    table,
    refetch: refetchTable,
  } = useTableDataAndFilters<
    CampaignFilters,
    CampaignTable,
    ExtraInfoCampaignHeader,
    ResCampaignsList
  >({
    filterAtom: campaignFilterBrandAtom,
    getColumns: getCampaignsTableHeaders,
    columnsExtras: { isInfluencer: isInfluencer() },
    responseAccessKey: 'campaigns',
    query: GET_CAMPAIGNS_LIST,
    subrowAccesor: 'users',
  });

  useEffect(() => {
    if (brand) {
      setFilters((prev) => ({
        ...prev,
        client: [{ label: '', value: brand?.id || '' }],
      }));
      refetchTable();
    }
    return () => {
      resetPagination();
      setFilters((prev) => ({
        ...prev,
        client: undefined,
      }));
    };
  }, [brand]);

  const handleTableClick = (idClick: string, influencer_id?: string) => {
    const campaign = campaigns.find((c) => c.id === idClick);
    if (campaign)
      navigate(
        replaceIds({
          url: ROUTES.SINGLE_CAMPAIGN_GENERAL,
          params: [
            { campaignId: campaign.id },
            { id: influencer_id || 'campaign' },
          ],
        }),
      );
  };

  return (
    <div className="w-full flex flex-col gap-y-5">
      <Breadcrumbs
        icon={<ProspectsIcon />}
        breadcrumbs={[
          { text: t('prospects.title'), link: ROUTES.PROSPECTS },
          {
            text: data?.brand?.data?.name || '',
            link: ROUTES.SINGLE_PROSPECT.replace(':id', brand.id),
          },
          { text: t('campaigns.title') },
        ]}
      />
      <div className="flex-row gap-x-2 overflow-x-auto customScroll pb-1 lg:hidden flex">
        <Tab
          to={ROUTES.SINGLE_PROSPECT.replace(':id', brand.id)}
          text={t('profile.menu.title1')}
        />
        <Tab
          to={ROUTES.SINGLE_PROSPECT_MEMBERS.replace(':id', brand.id)}
          text={t('profile.menu.members')}
        />
        <Tab
          to={ROUTES.SINGLE_PROSPECT_CAMPAIGNS.replace(':id', brand.id)}
          text={t('profile.menu.title2')}
        />
      </div>
      <div className="flex flex-row gap-12">
        {brand && <LateralProfileProspectMenu brand={brand} />}
        <div>
          <DataTable
            error={error}
            handleRowClick={(idClick) => handleTableClick(idClick)}
            handleSubrowClick={(idClick, influencer_id) =>
              handleTableClick(idClick, influencer_id)
            }
            loading={loading}
            table={table}
            total={total}
            tableId="ProspectCampaignsTable"
            sorting={{ ...filters }}
            SubrowsComponent={SubrowTable}
          />
        </div>
      </div>
    </div>
  );
};
